import {
  ATTACHMENT_FIELDS,
  COMPANY_BINDINGS,
  OWNER_BINDINGS,
  EMAIL_BINDINGS,
  RMP_BINDINGS,
  stateFieldContext,
  present,
} from '../data/state-rules-2025.ts';
export { facilityStateIssues } from './jurisdiction-requirements.ts';
import catalog from '../data/state-fields-2025.json' with { type: 'json' };
import { isSupportedStateField } from './state-field-identifiers.ts';
export { stateXmlName } from './state-field-identifiers.ts';
import type {
  StateFieldAnswers,
  StateFieldValue,
  TierIIStateReporting,
  TierIIDocumentKind,
} from '../../tri-shared/types/tierii-state-fields.ts';
import type { FacilityMeta } from './tier2submit-export.ts';
import type { TierIIReportFormEntry } from '../types/reporting.ts';

export type { StateFieldAnswers, StateFieldValue, TierIIStateReporting, TierIIDocumentKind };
export interface StateFieldDefinition {
  field: string;
  label: string;
  required: boolean;
  choices?: string[];
  choiceValues?: number[];
  radio?: boolean;
  searchLabel?: string;
  pretext?: string;
}
export interface StateDocumentStatus {
  fileCount?: number;
  kinds: TierIIDocumentKind[];
  sdsComplete: boolean;
  missingSds: string[];
}
export interface ResolvedStateField extends StateFieldDefinition {
  requested?: boolean;
  value: StateFieldValue;
  source?: string;
}
export const STATE_FIELDS_YEAR = catalog.reportingYear;
export const emptyStateReporting = (): TierIIStateReporting => ({ facility: {}, chemicals: {}, documents: [] });
export const reportEntryKey = (entry: TierIIReportFormEntry) =>
  entry.basis === 'compound' ? `compound-${entry.casOrCategory}` : `mixture-${entry.productID}`;
export function stateFieldDefinitions(
  scope: 'facility' | 'chemical',
  state: string,
  year = STATE_FIELDS_YEAR,
): StateFieldDefinition[] {
  if (year !== STATE_FIELDS_YEAR) return [];
  return (
    ((scope === 'facility' ? catalog.facilities : catalog.chemicals) as Record<string, StateFieldDefinition[]>)[
      state
    ] ?? []
  );
}
export function resolveFacilityStateFields(
  meta: FacilityMeta,
  documents?: StateDocumentStatus,
  year = meta.reportingYear ?? STATE_FIELDS_YEAR,
): ResolvedStateField[] {
  const state = meta.state;
  const saved = meta.stateReporting?.facility[state] ?? {};
  return stateFieldDefinitions('facility', state, year).map((def) => {
    let value: StateFieldValue = saved[def.field] ?? null;
    let source: string | undefined;
    const key = `${state}:${def.field}`;
    if (COMPANY_BINDINGS.includes(key)) {
      const company = meta.name;
      if (company) {
        value = company;
        source = 'Facility name';
      }
    }
    if (OWNER_BINDINGS.includes(key)) {
      const owner = meta.contacts?.find((c) => c.contactTypes.includes('Owner / Operator'))?.name;
      if (owner) {
        value = owner;
        source = 'Owner / operator contact';
      }
    }
    if (EMAIL_BINDINGS.includes(key)) {
      const email = meta.contacts?.find((c) => c.contactTypes.includes('Tier II Information Contact'))?.email;
      if (email) {
        value = email;
        source = 'Tier II information contact';
      }
    }
    if (RMP_BINDINGS.includes(key) && meta.subjectToSection112r != null) {
      value = key === 'NC:number02' ? (meta.subjectToSection112r ? 1 : 0) : meta.subjectToSection112r ? 'Yes' : 'No';
      source = 'CAA Section 112(r) answer';
    }
    const kind = ATTACHMENT_FIELDS[state]?.[def.field];
    if (kind) {
      const attached = kind === 'sds' ? documents?.sdsComplete === true : documents?.kinds.includes(kind) === true;
      value = def.field.startsWith('text') ? (attached ? 'Yes' : 'No') : attached;
      source = 'Report attachments';
    }
    return { ...def, value, ...(source ? { source } : {}) };
  });
}
export function resolveChemicalStateFields(
  meta: FacilityMeta,
  entry: TierIIReportFormEntry,
  year = meta.reportingYear ?? STATE_FIELDS_YEAR,
): ResolvedStateField[] {
  const saved = meta.stateReporting?.chemicals[meta.state]?.[reportEntryKey(entry)] ?? {};
  return stateFieldDefinitions('chemical', meta.state, year).map((def) => {
    const value = saved[def.field] ?? null;
    // These are inbound shipment facts. A delivery vessel is not the inventory storage vessel.
    // https://www.mass.gov/doc/hazconnect-system-users-guide/download
    return { ...def, value, ...(meta.state === 'MA' && entry.isEHS === false ? { required: false } : {}) };
  });
}
export function stateFieldsForForm(fields: ResolvedStateField[], state: string, scope: 'facility' | 'chemical') {
  const { required, requested } = stateFieldContext(fields, state, scope);
  return fields.map((field) => ({
    ...field,
    required: required.has(field.field),
    requested: requested.has(field.field),
  }));
}
export function stateFieldIssues(
  fields: ResolvedStateField[],
  state: string,
  scope: 'facility' | 'chemical',
): string[] {
  const { required, issues } = stateFieldContext(fields, state, scope);
  for (const field of fields) {
    if (!isSupportedStateField(field.field)) {
      issues.push(
        `Unsupported state field ${field.field} (${field.label}); update the state catalog integration before exporting.`,
      );
      continue;
    }
    const v = field.value;
    if (required.has(field.field) && !present(v)) {
      issues.push(`${field.label} is required.`);
      continue;
    }
    if (!present(v)) continue;
    const choices = field.choiceValues ?? field.choices;
    if (choices && !(choices as (string | number)[]).includes(v as string | number))
      issues.push(`Choose a listed value for ${field.label}.`);
    if (field.field.startsWith('number') && (!Number.isFinite(v) || typeof v !== 'number' || v < 0))
      issues.push(`${field.label} must be a nonnegative number.`);
    if (
      field.field.startsWith('date') &&
      (typeof v !== 'string' ||
        !/^\d{4}-\d{2}-\d{2}$/.test(v) ||
        !Number.isFinite(Date.parse(v)) ||
        new Date(v).toISOString().slice(0, 10) !== v)
    )
      issues.push(`${field.label} must be a valid date.`);
    if (field.field.startsWith('bool') && typeof v !== 'boolean')
      issues.push(`${field.label} must be answered Yes or No.`);
    if (
      scope === 'facility' &&
      ATTACHMENT_FIELDS[state]?.[field.field] &&
      required.has(field.field) &&
      v !== true &&
      v !== 'Yes'
    )
      issues.push(`A report attachment is needed: ${field.label.replace(/[.]$/, '')}.`);
  }
  return issues;
}
